/**
 * 大厅首屏：建房 / 进房两条路，加一个可改的昵称。**开局不需要注册、不需要登录**——
 * 身份在后台匿名拿（`useAuth`），用户从打开到进房只需要点两下。邮箱+密码
 * 都只是**可选的**「护住这个账号 / 换设备找回来」入口，藏在昵称行与「加入房间」旁边，
 * 不影响零摩擦开玩这条主路径。
 *
 * ── 布局（P1 / P2）─────────────────────────────────────────
 * 首屏是**弹性满屏**的：标题贴顶、两个入口坐在视觉重心、脚注贴底，中间的
 * `.lobby__gap` 吃掉全部剩余高度。屏幕更高时是气口变大，而不是「中间挤着滚、
 * 下面空一块」。底部区（dock）在首屏**什么都不放** —— `bar` 态高度由内容决定，
 * 两个入口本身就是屏幕主体。
 *
 * ── 两个入口为什么等大（DEVICES.md R3）────────────────────
 * 微信会屏蔽域名，「点链接进来」这条路随时可能失效，房间码才是唯一稳定的入口。
 * 把「加入房间」做成一行小字链接，等于在微信封域名那天把一半的人挡在门外。
 * 所以**尺寸相同、样式分级**：建房是填充色主 CTA，进房是描边的次要样式。
 *
 * 结构上仍然是 `Lobby`（stage 区）+ `LobbyDock`（dock 区）两个导出，
 * 对应 App 的两个 slot；两者靠 `useLobbyFlow` 共享「现在在哪一屏」。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { PublicAccount } from '@poker/shared'

import { useUiStore } from '../store/index.js'
import type { LocalSetup } from '../transport/LocalEngineTransport.js'
import { AvatarButton, AvatarEntrySheet, useAvatarEntry } from './AvatarEntry.js'
import { CreateRoom, CreateRoomDock } from './CreateRoom.js'
import { consumeInviteCode } from './env.js'
import { HistoryFindEntry, HistorySheet, useHistoryEntry } from './HistoryEntry.js'
import { JoinRoom, JoinRoomDock } from './JoinRoom.js'
import { getLobbyPort, startLobbyWatch, useLobbyFlow } from './lobbyFlow.js'
import { IconAlert } from './lobbyIcons.js'
import { pushNickname, shouldPushOnCommit } from './nicknameSync.js'
import { DEFAULT_LOCAL_SETUP_FORM, toLocalSetup } from './localSetup.js'
import {
  EmailAuthSheets,
  EmailBindEntry,
  EmailLoginEntry,
  useEmailAuthEntry,
} from './EmailAuthEntry.js'
import { currentNickname, randomNickname, truncateNickname, useAuth, useNicknameStore } from './useAuth.js'
import './lobby.css'
import { RecursiveRoom } from '../theme/RecursiveRoom.js'

export interface LobbyProps {
  /**
   * 本地传手机（单机试玩）的入口。
   * ★ 保留这个 prop 是为了不打断 `AppRoot` 现有的调用签名；联机才是主路径，
   *   所以它只在首屏底部占一行次要文字按钮。不传就完全不显示。
   */
  readonly onStart?: (setup: LocalSetup) => void
  /** 进入牌桌（默认切 `uiStore.view`）。接线层想自己控制路由时覆盖它。 */
  readonly onEnterTable?: () => void
  /** 覆盖微信判定（测试用） */
  readonly wechat?: boolean
}

// ============================================================================
// 公共小件
// ============================================================================

/**
 * 昵称：牌桌上别人看到的就是它。改名不需要任何网络请求。
 * 前面那颗小圆头像同一行：点了开头像选择面板（`AvatarEntry.tsx`），换头像与换名字
 * 是同一件「我是谁」的两半，放在一起比拆成两行更说得通。
 */
function NicknameRow({ account }: { readonly account: PublicAccount | null }): ReactNode {
  const nickname = useNicknameStore((s) => s.nickname)
  const setNickname = useNicknameStore((s) => s.setNickname)

  /**
   * 把昵称存到账号上。
   *
   * ★ 时机选「失焦」和「点换一个」，不是 onChange —— 后者每敲一个字打一次接口。
   * ★ 尽力而为，失败不打扰：本地那份仍然有效（建房/进房时昵称是随消息带上去的），
   *   名字保持「没存上」，下次失焦或下次对齐再补。这是个「顺手把它也存到云上」的动作，
   *   不是用户主动发起的操作，为它弹一条错误提示是喧宾夺主。
   * ★ 只存改过的名字（`shouldPushOnCommit`）；点一下又点走、没改过，就什么也不发。
   *   同一个名字重复触发由 `pushNickname` 的队列合并。
   */
  const commit = useCallback((): void => {
    if (account === null) return // 身份还没准备好；账号一落地就会对齐并补存
    const state = useNicknameStore.getState()
    if (!shouldPushOnCommit(state)) return
    pushNickname(account.id, state.nickname).catch(() => undefined)
  }, [account])
  // 草稿是**原样**的输入：store 里存的是 sanitize 之后的版本，
  // 直接把 sanitize 结果回填输入框会让人打不出中间的空格（刚敲下就被 trim 掉）
  const [draft, setDraft] = useState(nickname)
  const inputRef = useRef<HTMLInputElement>(null)
  // ★ 名字被别处换掉（登录换号、别的设备改过名）时输入框要跟上 —— 草稿只在挂载时取一次，
  //   不跟的话 store 和发出去的都是新名字，输入框里却还是旧的。正在输入时不动，免得吞掉半截草稿
  useEffect(() => {
    if (inputRef.current !== null && inputRef.current === globalThis.document?.activeElement) return
    setDraft(nickname)
  }, [nickname])
  const avatarEntry = useAvatarEntry()

  return (
    // 一行轻控件而不是一张卡片：昵称只是个可改的默认值，不该和两个入口抢视觉重量
    <div className="lobby-ident">
      <AvatarButton account={account} onOpen={avatarEntry.show} />
      <input
        ref={inputRef}
        className="lobby-ident__input"
        value={draft}
        inputMode="text"
        name="nickname"
        spellCheck={false}
        autoComplete="off"
        aria-label="昵称（牌桌上别人看到的名字）"
        onChange={(e) => {
          const next = truncateNickname(e.target.value)
          setDraft(next)
          setNickname(next)
        }}
        onBlur={() => {
          setDraft(useNicknameStore.getState().nickname)
          commit()
        }}
      />
      <button
        type="button"
        className="lobby-ident__swap"
        onClick={() => {
          const next = randomNickname()
          setDraft(next)
          setNickname(next)
          commit()
        }}
      >
        换一个
      </button>
      <AvatarEntrySheet state={avatarEntry} account={account} />
    </div>
  )
}

// ============================================================================
// 首屏
// ============================================================================

function Home({ onStart }: { readonly onStart?: (setup: LocalSetup) => void }): ReactNode {
  const go = useLobbyFlow((s) => s.go)
  const auth = useAuth()
  // 邮箱+密码：切设备的另一条路，不需要先在旧设备上生成任何东西
  const emailAuth = useEmailAuthEntry(auth.account)
  const history = useHistoryEntry()

  return (
    <>
      {/* ★ 标题不写「德州扑克」：顶栏常驻着同样四个字，写两遍只是重复。
          也不解释「各用各的手机」这种产品形态 —— 打开一个扑克网站，
          当然是用自己的手机，说出来反而奇怪。标题就是一句召唤。 */}
      <div className="club-intro">
        <div className="club-intro__copy">
          <span className="club-eyebrow">THE GÖDEL ROOM · 私人牌室</span>
          <h1>每一手，<br /><span>无限可能。</span></h1>
          <p>确定的规则，无限的博弈。<br />留个座位，和朋友慢慢过招。</p>
          <div className="club-edition" aria-hidden="true"><span>01 / INFINITE PLAY</span><svg width="20" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C8 7 3 10 3 15a5 5 0 0 0 8 3c0 2-1 3-3 4h8c-2-1-3-2-3-4a5 5 0 0 0 8-3c0-5-5-8-9-13Z" /></svg></div>
        </div>
        <div className="club-intro__art"><RecursiveRoom /><span>FIG. 01 — THE ENDLESS ROOM</span></div>
      </div>

      {/* 剩余高度归这一段：标题在顶、「我是谁 + 去哪」成为下方的一组，
          入口因此落在拇指够得到的位置。中间宁可留白，也不再塞一条说明 ——
          两个按钮已经把这东西怎么用说完了。 */}
      <div className="lobby__gap" />

      <div className="club-section-label"><span>入席之前</span><span>YOUR SEAT AWAITS</span></div>
      <NicknameRow account={auth.account} />

      {/* ★ 两个入口等大：房间码是第一入口（R3），不能把「加入」藏成小字；
          但样式分级 —— 建房是这一屏唯一的填充色主 CTA。 */}
      <div className="lobby__cta">
        <button
          type="button"
          className="lobby-big lobby-big--create pressable"
          onClick={() => go('create')}
        >
          <span className="club-entry-no" aria-hidden="true">01</span>
          <span className="club-entry-copy"><span className="lobby-big__title">创建房间</span><span>做东，邀朋友入席</span></span>
          <span className="club-entry-arrow" aria-hidden="true">↗</span>
        </button>

        <button
          type="button"
          className="lobby-big lobby-big--join pressable"
          onClick={() => go('join')}
        >
          <span className="club-entry-no" aria-hidden="true">02</span>
          <span className="club-entry-copy"><span className="lobby-big__title">加入房间</span><span>输入房间码，赴一场牌局</span></span>
          <span className="club-entry-arrow" aria-hidden="true">↗</span>
        </button>
      </div>

      {auth.status === 'error' && (
        <div className="lobby-error" role="alert">
          <IconAlert size={18} />
          <span>
            {/* 服务端那句原始错误是给开发者看的，不往界面上抬 */}
            连不上服务器
            {/* 视觉仍然是 32px 的轻按钮（它嵌在一行说明文字里，撑到 44 会把整段顶散），
                命中区靠 .hit-expand 的伪元素补到 44×44 —— 实测这里原本只有 50×32，
                是全大厅唯一一个真的点不满的目标。 */}
            <button
              type="button"
              className="lobby-btn lobby-btn--ghost hit-expand"
              style={{ minHeight: 32, padding: '0 var(--sp-2)' }}
              onClick={auth.retry}
            >
              重试
            </button>
          </span>
        </div>
      )}
      {/* 连接状态顶栏一直挂着（同一句「连接中…」），这里不再写第二遍 */}

      <div className="lobby__gap lobby__gap--sm" />

      {/* 四个低频入口属于同一层级，固定为一行，不让最后一个孤零零掉到第二行。 */}
      <div className="lobby-utility-bar" role="group" aria-label="其他功能">
        {/* 历史牌局：身份没准备好之前查了也是空的，不如不出现 */}
        {auth.account !== null && <HistoryFindEntry onOpen={history.show} />}
        {onStart !== undefined && (
          <button
            type="button"
            className="lobby-btn lobby-btn--ghost"
            onClick={() => onStart(toLocalSetup(DEFAULT_LOCAL_SETUP_FORM))}
          >
            本地同玩
          </button>
        )}
        <EmailBindEntry bound={emailAuth.bound} onOpen={() => emailAuth.open('bind')} />
        <EmailLoginEntry onOpen={() => emailAuth.open('login')} />
      </div>

      {/* 尾部也留一段气口：删掉三步说明后富余的高度按 2:1 分给标题下方与屏幕底部，
          剩下的是呼吸感，而不是标题下面一整块 400px 的黑。 */}
      <div className="lobby__gap lobby__gap--half" />

      <EmailAuthSheets state={emailAuth} />
      <HistorySheet state={history} />
    </>
  )
}

// ============================================================================
// 导出：stage / dock 两个 slot
// ============================================================================

/**
 * 进房成功后切到牌桌。副作用集中在这一个 hook 里，dock 那边不用重复一遍。
 *
 * ★ 只在 false → true 的**跃变**上触发，不在挂载时触发：
 *   否则「人还在房间里，但想回大厅看看」会被立刻弹回牌桌，按返回都退不出来。
 */
function useEnterTableOnJoin(onEnterTable?: () => void): void {
  const entered = useLobbyFlow((s) => s.entered)
  const setView = useUiStore((s) => s.setView)
  const prev = useRef(entered)

  useEffect(() => {
    const was = prev.current
    prev.current = entered
    if (!entered || was) return
    if (onEnterTable === undefined) setView('table')
    else onEnterTable()
  }, [entered, onEnterTable, setView])
}

/**
 * 「点邀请链接进来」这条路：把链接里的码填进输入框，能发就直接发。
 *
 * ★ 即使链接这条路走通了，进房页也照样把 6 格输入框摆在那里（R3：链接不可依赖，
 *   码才是第一入口）。这里只是省掉一次手输，不是另起一条隐藏路径。
 */
function useInviteLink(): void {
  useEffect(() => {
    const code = consumeInviteCode()
    if (code === null) return
    const flow = useLobbyFlow.getState()
    flow.setLastCode(code)
    flow.go('join')
    // 连接还没建好就只预填：这时提交只会得到一句「连不上服务器」的噪音，
    // 用户看到码已经填好，点一下 dock 上的按钮即可
    if (getLobbyPort() !== null) flow.submitJoin(code, currentNickname())
  }, [])
}

export function Lobby({ onStart, onEnterTable, wechat }: LobbyProps = {}): ReactNode {
  const screen = useLobbyFlow((s) => s.screen)

  // net → flow 的桥。挂载一次；组件卸载即退订，不会在牌桌里继续跑
  useEffect(() => startLobbyWatch(), [])
  useInviteLink()
  useEnterTableOnJoin(onEnterTable)

  // 建房 / 进房屏自己就是一个完整的 `.lobby` 弹性列，微信条塞进它的第一行，
  // 免得像旧版那样再包一层 padding，叠出一道空带
  if (screen === 'create') return <CreateRoom />
  if (screen === 'join') return <JoinRoom />

  return (
    <div className="lobby">
      <Home {...(onStart === undefined ? {} : { onStart })} />
    </div>
  )
}

export function LobbyDock({ onEnterTable }: LobbyProps = {}): ReactNode {
  const screen = useLobbyFlow((s) => s.screen)
  const auth = useAuth()
  const blocked = auth.status === 'error'

  if (screen === 'create') {
    return <CreateRoomDock disabled={blocked} {...(onEnterTable === undefined ? {} : { onEnter: onEnterTable })} />
  }
  if (screen === 'join') return <JoinRoomDock />

  // 首屏的两个入口本身就是屏幕主体：dock 上既不放第三个按钮，也不放说明文字。
  // 「房间码里没有哪些字符」是实现细节，输错时自动纠正并轻提示即可（见 CodeInput）。
  return null
}

export default Lobby
